import {
  getTrackingTokenFromLocalStorage,
  isInAppBrowser,
} from 'helpers/browser';
import { KustomerMain } from 'core/types';
import { getStore, updateStore } from 'core_main/store';
import { fetchSchedules } from 'core_main/api/schedules';
import { fetchCurrentTrackingToken } from 'core_main/api/tracking';
import { fetchSettings } from 'core_main/api/settings';
// eslint-disable-next-line import/no-cycle
import { importAndInitializePubNub } from 'core_main/globals/vendors';
import { handleError, ErrorMessage } from 'globals/errors';
import { initSentry } from 'globals/sentry';
import { createInitCallbackResponse } from './helpers';

/*
  This initializes the Core SDK by fetching the chat configuration and initializing pubnub
  Pubnub will only be initialized if the following is true (flowchart https://miro.com/app/board/o9J_kuhgNHQ=/) :
    - The tracking token exists in local storage
    - The tracking token is connected to a customerId

*/
export const init: KustomerMain['init'] = (parameters?, cb?) => {
  const originalTrackingToken = getTrackingTokenFromLocalStorage();

  // Always set testAutomationId (either to provided value or undefined)
  // This ensures clean state between init calls
  updateStore('testAutomationId', parameters?.testAutomationId);
  updateStore('useLegacyConversationPreview', parameters?.useLegacyConversationPreview ?? false);

  // Set enableUrlUnfurling flag (defaults to true; consumers may pass false to opt out)
  updateStore('enableUrlUnfurling', parameters?.enableUrlUnfurling ?? true);

  return Promise.all([
    fetchSettings({
      brandId: parameters?.brandId,
      lang: parameters?.lang,
      assistantId: parameters?.assistantId,
    }),
    fetchSchedules(parameters?.scheduleId),
  ])
    .then((response) => {
      const sentryKey = response[0]?.data?.attributes?.sentry?.web;
      if (sentryKey && !isInAppBrowser()) {
        initSentry(sentryKey, getStore().orgId);
      }

      const { enabled } = getStore().settings;
      if (!enabled) {
        const message = ErrorMessage.CHAT_DISABLED;

        console.warn(message);

        return Promise.reject(new Error(message));
      }

      if (originalTrackingToken === null) {
        updateStore('isPubNubConnected', false);
        return undefined;
      }

      return fetchCurrentTrackingToken().then((response) => {
        const customerId = response?.data?.relationships?.customer?.data?.id;

        updateStore('customerId', customerId);

        if (customerId === undefined) {
          updateStore('isPubNubConnected', false);
          return undefined;
        }

        const { orgId } = getStore();

        const callbackResponse = createInitCallbackResponse();
        return importAndInitializePubNub({
          customerId,
          orgId,
          cb: () => {
            if (typeof cb === 'function') cb(callbackResponse);
          },
        }).then(() => callbackResponse);
      });
    })
    .then((callbackResponse) => {
      // if we already built the callbackResponse it means we passed the callback to pubnub, so don't call it here
      if (callbackResponse) return;
      if (typeof cb === 'function') {
        const callbackResponse = createInitCallbackResponse();
        cb(callbackResponse);
      }
    })
    .catch((error) => {
      if (typeof cb === 'function') {
        cb(null, error);
      }
      handleError(error, { message: 'Failed to initialize' });
    });
};
